/**
 * CRMP V2 Page - AURA Advanced Unified Regulatory Architecture
 * 
 * New Architecture:
 * Regulatory Universe → CRMP → Controls → Monitoring → Evidence → Findings → Actions
 * 
 * Key Principles:
 * 1. Universe determines applicability (what applies)
 * 2. CRMP manages implementation (how the institution manages it)
 * 3. Controls are institution-defined, not auto-generated
 * 4. Full traceability from Control → Requirement → Universe → Library
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardText, Shield, Buildings, ArrowRight,
  FileText, ChartBar, Database, Robot,
  Target, Warning, Lightning, BookOpen,
  CaretRight, CaretDown, MagnifyingGlass, Plus, Clock, Users,
  ChartLine, Gauge, FolderOpen, Brain, X, Check,
  ArrowsClockwise, Funnel, TreeStructure, ListChecks,
  ShieldCheck, CheckCircle, Eye, ArrowLeft, Link as LinkIcon,
  Folder, FilePdf, PlusCircle, DotsThree, Note, Trash,
  Export, DownloadSimple, Rows, Table, Globe, Scales,
  Sparkle, CloudArrowUp, Bell, ArrowSquareOut, Tag
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';
import PageBreadcrumb from '@/components/PageBreadcrumb';

// ============================================================
// CRMP V2 MAIN PAGE
// ============================================================

const CRMPV2Page = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Core state
  const [activeView, setActiveView] = useState(searchParams.get('view') || 'overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [controls, setControls] = useState([]);
  const [findings, setFindings] = useState([]);
  const [regulatoryChanges, setRegulatoryChanges] = useState([]);
  const [evidence, setEvidence] = useState([]);
  
  // Filters
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showBuildCRMPModal, setShowBuildCRMPModal] = useState(false);
  const [showAddRequirementModal, setShowAddRequirementModal] = useState(false);
  const [showAddControlModal, setShowAddControlModal] = useState(false);
  
  // Selected for detail view
  const [selectedRequirement, setSelectedRequirement] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  useEffect(() => {
    if (activeView !== 'overview') {
      setSearchParams({ view: activeView });
    } else {
      setSearchParams({});
    }
  }, [activeView]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, reqsRes, controlsRes, findingsRes, changesRes, evidenceRes] = await Promise.all([
        axios.get(`${API}/crmp/v2/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crmp/v2/requirements?limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crmp/v2/controls?limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crmp/v2/findings?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crmp/v2/regulatory-changes?limit=50`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { changes: [] } })),
        axios.get(`${API}/crmp/v2/evidence?limit=50`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { evidence: [] } }))
      ]);
      
      setStats(statsRes.data);
      setRequirements(reqsRes.data.requirements || []);
      setControls(controlsRes.data.controls || []);
      setFindings(findingsRes.data.findings || []);
      setRegulatoryChanges(changesRes.data.changes || []);
      setEvidence(evidenceRes.data.evidence || []);
    } catch (error) {
      console.error('Failed to fetch CRMP data:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load CRMP data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Filtered requirements
  const filteredRequirements = useMemo(() => {
    let filtered = requirements;
    
    if (selectedTheme !== 'all') {
      filtered = filtered.filter(r => r.regulatory_theme_id === selectedTheme);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.title?.toLowerCase().includes(query) ||
        r.requirement_id?.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [requirements, selectedTheme, searchQuery]);

  // Navigation tabs
  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBar },
    { id: 'requirements', label: 'Requirements', icon: FileText, count: requirements.length },
    { id: 'risks', label: 'Risks', icon: Warning, count: stats?.risks?.total || 0 },
    { id: 'controls', label: 'Controls', icon: ShieldCheck, count: controls.length },
    { id: 'monitoring', label: 'Monitoring', icon: Gauge, count: stats?.monitoring?.total || 0 },
    { id: 'evidence', label: 'Evidence', icon: CloudArrowUp, count: evidence.length },
    { id: 'findings', label: 'Findings & Actions', icon: Target, count: findings.length },
    { id: 'changes', label: 'Reg Changes', icon: Bell, count: regulatoryChanges.filter(c => c.status === 'pending').length }
  ];

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1 flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-background border-b border-border">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center">
                  <ClipboardText size={28} className="text-gold" weight="duotone" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-medium text-text-primary">
                    Compliance Risk Management Plan
                  </h1>
                  <p className="text-sm text-text-secondary">
                    How the institution manages applicable regulatory requirements
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Link
                  to="/regulatory-universe"
                  className="px-4 py-2 bg-background border border-border text-text-secondary rounded-lg hover:border-gold/50 transition-all flex items-center gap-2"
                >
                  <Globe size={18} />
                  Regulatory Universe
                </Link>
                <button
                  onClick={() => setShowBuildCRMPModal(true)}
                  className="px-4 py-2 bg-gold text-background rounded-lg hover:bg-gold/90 transition-all flex items-center gap-2 font-medium"
                  data-testid="build-crmp-btn"
                >
                  <PlusCircle size={18} />
                  Build CRMP
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-border -mb-px">
              {tabs.map(tab => {
                const TabIcon = tab.icon;
                const isActive = activeView === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveView(tab.id)}
                    className={`px-4 py-3 text-sm font-medium flex items-center gap-2 transition-all border-b-2 -mb-[2px] ${
                      isActive
                        ? 'text-gold border-gold'
                        : 'text-text-secondary hover:text-text-primary border-transparent hover:border-border'
                    }`}
                    data-testid={`crmp-tab-${tab.id}`}
                  >
                    <TabIcon size={16} />
                    {tab.label}
                    {tab.count !== undefined && (
                      <span className={`px-1.5 py-0.5 text-xs rounded ${
                        isActive ? 'bg-gold/20 text-gold' : 'bg-background text-text-tertiary'
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <ArrowsClockwise size={32} className="animate-spin text-gold" />
            </div>
          ) : activeView === 'overview' ? (
            <CRMPOverviewTab 
              stats={stats} 
              requirements={requirements}
              controls={controls}
              findings={findings}
              onBuildCRMP={() => setShowBuildCRMPModal(true)}
            />
          ) : activeView === 'requirements' ? (
            <RequirementsTab
              requirements={filteredRequirements}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedTheme={selectedTheme}
              setSelectedTheme={setSelectedTheme}
              onAddNew={() => setShowAddRequirementModal(true)}
              onSelect={setSelectedRequirement}
              onRefresh={fetchDashboardData}
            />
          ) : activeView === 'controls' ? (
            <ControlsTab
              controls={controls}
              requirements={requirements}
              onAddNew={() => setShowAddControlModal(true)}
              onRefresh={fetchDashboardData}
            />
          ) : activeView === 'monitoring' ? (
            <MonitoringTab
              stats={stats}
              controls={controls}
              onRefresh={fetchDashboardData}
            />
          ) : activeView === 'findings' ? (
            <FindingsTab
              findings={findings}
              onRefresh={fetchDashboardData}
            />
          ) : activeView === 'evidence' ? (
            <EvidenceTab
              evidence={evidence}
              requirements={requirements}
              controls={controls}
              onRefresh={fetchDashboardData}
            />
          ) : activeView === 'changes' ? (
            <RegulatoryChangesTab
              changes={regulatoryChanges}
              onRefresh={fetchDashboardData}
            />
          ) : (
            <div className="p-6 text-center py-20">
              <Database size={64} className="mx-auto text-text-tertiary mb-4 opacity-30" />
              <h3 className="text-lg font-semibold text-text-primary mb-2">{tabs.find(t => t.id === activeView)?.label}</h3>
              <p className="text-text-secondary">This section is under development</p>
            </div>
          )}
        </div>
      </div>

      {/* Build CRMP Modal */}
      <AnimatePresence>
        {showBuildCRMPModal && (
          <BuildCRMPModal
            onClose={() => setShowBuildCRMPModal(false)}
            onSuccess={() => {
              setShowBuildCRMPModal(false);
              fetchDashboardData();
            }}
          />
        )}
      </AnimatePresence>

      {/* Add Requirement Modal */}
      <AnimatePresence>
        {showAddRequirementModal && (
          <AddRequirementModal
            onClose={() => setShowAddRequirementModal(false)}
            onSuccess={() => {
              setShowAddRequirementModal(false);
              fetchDashboardData();
            }}
          />
        )}
      </AnimatePresence>

      {/* Add Control Modal */}
      <AnimatePresence>
        {showAddControlModal && (
          <AddControlModal
            requirements={requirements}
            onClose={() => setShowAddControlModal(false)}
            onSuccess={() => {
              setShowAddControlModal(false);
              fetchDashboardData();
            }}
          />
        )}
      </AnimatePresence>

      {/* Requirement Detail Drawer */}
      <AnimatePresence>
        {selectedRequirement && (
          <RequirementDetailDrawer
            requirementId={selectedRequirement}
            onClose={() => setSelectedRequirement(null)}
            onRefresh={fetchDashboardData}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================
// OVERVIEW TAB
// ============================================================

const CRMPOverviewTab = ({ stats, requirements, controls, findings, onBuildCRMP }) => {
  const coverage = stats?.regulatory_coverage || {};
  const riskData = stats?.risks || {};
  const controlData = stats?.controls || {};
  const effectiveness = stats?.effectiveness || {};
  const findingData = stats?.findings || {};

  return (
    <div className="p-6 space-y-6">
      {/* Regulatory Coverage - THE KEY METRIC */}
      <div className="bg-gradient-to-r from-gold/10 to-transparent border border-gold/30 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <Target size={20} className="text-gold" />
              Regulatory Coverage
            </h3>
            <p className="text-sm text-text-secondary mt-1">
              Of everything in your Regulatory Universe that requires operational compliance, how much is covered by the CRMP?
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-mono font-bold text-gold">
              {coverage.coverage_percentage?.toFixed(1) || 0}%
            </div>
            <p className="text-sm text-text-tertiary">
              {coverage.crmp_requirements || 0} / {coverage.universe_requirements || 0} requirements
            </p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4 h-3 bg-background rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-gold to-amber-400 transition-all duration-500"
            style={{ width: `${coverage.coverage_percentage || 0}%` }}
          />
        </div>
        
        {coverage.uncovered > 0 && (
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-amber-400">
              {coverage.uncovered} requirements not yet in CRMP
            </span>
            <button
              onClick={onBuildCRMP}
              className="px-3 py-1.5 bg-gold/10 text-gold rounded-lg text-sm font-medium hover:bg-gold/20 transition-all flex items-center gap-2"
            >
              <Plus size={14} />
              Add Missing Requirements
            </button>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        {/* Risks */}
        <div className="p-5 bg-surface border border-border rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
              <Warning size={22} className="text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-mono font-bold text-text-primary">{riskData.total || 0}</p>
              <p className="text-xs text-text-tertiary">Risks Identified</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            {Object.entries(riskData.by_rating || {}).slice(0, 3).map(([rating, count]) => (
              <span key={rating} className={`px-2 py-0.5 rounded ${
                rating === 'high' ? 'bg-red-500/10 text-red-400' :
                rating === 'medium' ? 'bg-amber-500/10 text-amber-400' :
                'bg-blue-500/10 text-blue-400'
              }`}>
                {count} {rating}
              </span>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="p-5 bg-surface border border-border rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
              <ShieldCheck size={22} className="text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-mono font-bold text-text-primary">{controlData.total || 0}</p>
              <p className="text-xs text-text-tertiary">Controls</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
              {controlData.implemented || 0} implemented
            </span>
            {controlData.gaps > 0 && (
              <span className="px-2 py-0.5 rounded bg-red-500/10 text-red-400">
                {controlData.gaps} gaps
              </span>
            )}
          </div>
        </div>

        {/* Control Effectiveness */}
        <div className="p-5 bg-surface border border-border rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Gauge size={22} className="text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-mono font-bold text-text-primary">
                {effectiveness.effective || 0}/{(effectiveness.effective || 0) + (effectiveness.partially_effective || 0) + (effectiveness.ineffective || 0) + (effectiveness.not_tested || 0)}
              </p>
              <p className="text-xs text-text-tertiary">Effective Controls</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-400">
              {effectiveness.partially_effective || 0} partial
            </span>
            <span className="px-2 py-0.5 rounded bg-gray-500/10 text-gray-400">
              {effectiveness.not_tested || 0} not tested
            </span>
          </div>
        </div>

        {/* Findings */}
        <div className="p-5 bg-surface border border-border rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
              <Target size={22} className="text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-mono font-bold text-text-primary">{findingData.open || 0}</p>
              <p className="text-xs text-text-tertiary">Open Findings</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-0.5 rounded bg-red-500/10 text-red-400">
              {findingData.overdue || 0} overdue
            </span>
            <span className="px-2 py-0.5 rounded bg-gray-500/10 text-gray-400">
              {findingData.total || 0} total
            </span>
          </div>
        </div>
      </div>

      {/* CRMP Requirements Table */}
      <div className="bg-surface border border-border rounded-xl">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-text-primary flex items-center gap-2">
            <FileText size={18} className="text-gold" />
            CRMP Requirements
          </h3>
          <Link
            to="/crmp-v2?view=requirements"
            className="text-sm text-gold hover:underline flex items-center gap-1"
          >
            View All <CaretRight size={14} />
          </Link>
        </div>
        
        {requirements.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background text-xs text-text-tertiary uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Requirement</th>
                  <th className="px-4 py-3 text-left w-48">Regulatory Source</th>
                  <th className="px-4 py-3 text-center w-20">Risks</th>
                  <th className="px-4 py-3 text-center w-24">Controls</th>
                  <th className="px-4 py-3 text-center w-28">Effectiveness</th>
                  <th className="px-4 py-3 text-center w-24">Findings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {requirements.slice(0, 10).map(req => (
                  <RequirementRow key={req.id} requirement={req} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <ClipboardText size={48} className="mx-auto text-text-tertiary mb-4 opacity-30" />
            <h4 className="font-medium text-text-primary mb-2">No CRMP Requirements Yet</h4>
            <p className="text-sm text-text-secondary mb-4">
              Add requirements from your Regulatory Universe to start building your CRMP
            </p>
            <button
              onClick={onBuildCRMP}
              className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-all inline-flex items-center gap-2"
            >
              <Plus size={16} />
              Build CRMP from Regulatory Universe
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Requirement Row for table
const RequirementRow = ({ requirement: req }) => {
  const navigate = useNavigate();
  
  // Get effectiveness from controls
  const controlCount = req.control_count || 0;
  const riskCount = req.risk_count || 0;
  const findingCount = req.finding_count || 0;
  
  return (
    <tr 
      className="hover:bg-background/50 cursor-pointer transition-colors"
      onClick={() => navigate(`/crmp-v2?view=requirements&req=${req.id}`)}
    >
      <td className="px-4 py-3">
        <div>
          <span className="text-xs font-mono text-gold">{req.requirement_id}</span>
          <p className="text-sm font-medium text-text-primary mt-0.5">{req.title}</p>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-text-secondary">
          {req.regulatory_basis?.[0]?.document_name || 'Regulatory Universe'}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
          riskCount > 0 ? 'bg-orange-500/10 text-orange-400' : 'bg-gray-500/10 text-gray-400'
        }`}>
          {riskCount}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
          controlCount > 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-500/10 text-gray-400'
        }`}>
          {controlCount}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-500/10 text-gray-400">
          Not Tested
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
          findingCount > 0 ? 'bg-red-500/10 text-red-400' : 'bg-gray-500/10 text-gray-400'
        }`}>
          {findingCount}
        </span>
      </td>
    </tr>
  );
};

// ============================================================
// REQUIREMENTS TAB
// ============================================================

const RequirementsTab = ({ 
  requirements, 
  searchQuery, 
  setSearchQuery, 
  selectedTheme, 
  setSelectedTheme,
  onAddNew,
  onSelect,
  onRefresh
}) => {
  return (
    <div className="p-6 space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
          <input
            type="text"
            placeholder="Search requirements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
          />
        </div>
        
        <button
          onClick={onRefresh}
          className="p-2 hover:bg-surface rounded-lg text-text-tertiary hover:text-text-primary transition-all"
        >
          <ArrowsClockwise size={18} />
        </button>
        
        <button
          onClick={onAddNew}
          className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-all flex items-center gap-2"
        >
          <Plus size={16} />
          Add Requirement
        </button>
      </div>

      {/* Requirements List */}
      {requirements.length > 0 ? (
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-background text-xs text-text-tertiary uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Requirement</th>
                <th className="px-4 py-3 text-left w-48">Regulatory Source</th>
                <th className="px-4 py-3 text-center w-20">Risks</th>
                <th className="px-4 py-3 text-center w-24">Controls</th>
                <th className="px-4 py-3 text-center w-28">Effectiveness</th>
                <th className="px-4 py-3 text-center w-24">Findings</th>
                <th className="px-4 py-3 text-center w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {requirements.map(req => (
                <tr 
                  key={req.id}
                  className="hover:bg-background/50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onSelect(req.id)}
                      className="text-left"
                    >
                      <span className="text-xs font-mono text-gold">{req.requirement_id}</span>
                      <p className="text-sm font-medium text-text-primary mt-0.5 hover:text-gold transition-colors">
                        {req.title}
                      </p>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-text-secondary">
                      {req.regulatory_basis?.[0]?.document_name || 'Regulatory Universe'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      req.risk_count > 0 ? 'bg-orange-500/10 text-orange-400' : 'bg-gray-500/10 text-gray-400'
                    }`}>
                      {req.risk_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      req.control_count > 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-500/10 text-gray-400'
                    }`}>
                      {req.control_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-500/10 text-gray-400">
                      Not Tested
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      req.finding_count > 0 ? 'bg-red-500/10 text-red-400' : 'bg-gray-500/10 text-gray-400'
                    }`}>
                      {req.finding_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => onSelect(req.id)}
                      className="p-1.5 text-text-tertiary hover:text-gold hover:bg-gold/10 rounded transition-colors"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-xl p-12 text-center">
          <FileText size={48} className="mx-auto text-text-tertiary mb-4 opacity-30" />
          <h3 className="font-medium text-text-primary mb-2">No Requirements Found</h3>
          <p className="text-sm text-text-secondary mb-4">
            {searchQuery ? 'Try adjusting your search' : 'Add requirements from your Regulatory Universe'}
          </p>
          <button
            onClick={onAddNew}
            className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-all inline-flex items-center gap-2"
          >
            <Plus size={16} />
            Add Requirement
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================
// CONTROLS TAB
// ============================================================

const ControlsTab = ({ controls, requirements, onAddNew, onRefresh }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredControls = useMemo(() => {
    if (!searchQuery) return controls;
    const query = searchQuery.toLowerCase();
    return controls.filter(c => 
      c.name?.toLowerCase().includes(query) ||
      c.control_id?.toLowerCase().includes(query) ||
      c.description?.toLowerCase().includes(query)
    );
  }, [controls, searchQuery]);

  return (
    <div className="p-6 space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
          <input
            type="text"
            placeholder="Search controls..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
          />
        </div>
        
        <button
          onClick={onRefresh}
          className="p-2 hover:bg-surface rounded-lg text-text-tertiary hover:text-text-primary transition-all"
        >
          <ArrowsClockwise size={18} />
        </button>
        
        <button
          onClick={onAddNew}
          className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-all flex items-center gap-2"
        >
          <Plus size={16} />
          Create Control
        </button>
      </div>

      {/* Controls Grid */}
      {filteredControls.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredControls.map(control => (
            <ControlCard key={control.id} control={control} />
          ))}
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-xl p-12 text-center">
          <ShieldCheck size={48} className="mx-auto text-text-tertiary mb-4 opacity-30" />
          <h3 className="font-medium text-text-primary mb-2">No Controls Found</h3>
          <p className="text-sm text-text-secondary mb-4">
            {searchQuery ? 'Try adjusting your search' : 'Create controls for your CRMP requirements'}
          </p>
          <button
            onClick={onAddNew}
            className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-all inline-flex items-center gap-2"
          >
            <Plus size={16} />
            Create Control
          </button>
        </div>
      )}
    </div>
  );
};

const ControlCard = ({ control }) => {
  const typeColors = {
    preventive: 'bg-blue-500/10 text-blue-400',
    detective: 'bg-amber-500/10 text-amber-400',
    corrective: 'bg-emerald-500/10 text-emerald-400'
  };
  
  const statusColors = {
    implemented: 'bg-emerald-500/10 text-emerald-400',
    design: 'bg-amber-500/10 text-amber-400',
    not_implemented: 'bg-red-500/10 text-red-400'
  };
  
  const effectivenessColors = {
    effective: 'bg-emerald-500/10 text-emerald-400',
    partially_effective: 'bg-amber-500/10 text-amber-400',
    ineffective: 'bg-red-500/10 text-red-400',
    not_tested: 'bg-gray-500/10 text-gray-400'
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-5 hover:border-gold/30 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={20} className="text-gold" />
          <span className="text-xs font-mono text-text-tertiary">{control.control_id}</span>
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${statusColors[control.status] || statusColors.design}`}>
          {control.status?.replace('_', ' ') || 'Design'}
        </span>
      </div>
      
      <h4 className="font-medium text-text-primary mb-2">{control.name}</h4>
      <p className="text-sm text-text-secondary line-clamp-2 mb-3">{control.description}</p>
      
      <div className="flex flex-wrap gap-2 mb-3">
        <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${typeColors[control.control_type] || typeColors.preventive}`}>
          {control.control_type}
        </span>
        <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${effectivenessColors[control.effectiveness_rating] || effectivenessColors.not_tested}`}>
          {control.effectiveness_rating?.replace('_', ' ') || 'Not Tested'}
        </span>
      </div>
      
      <div className="flex items-center justify-between text-xs text-text-tertiary pt-3 border-t border-border">
        <span>{control.owner_name || 'No owner'}</span>
        <span className="flex items-center gap-1">
          <LinkIcon size={12} />
          {control.requirement_ids?.length || 0} requirements
        </span>
      </div>
    </div>
  );
};

// ============================================================
// MONITORING TAB
// ============================================================

const MonitoringTab = ({ stats, controls, onRefresh }) => {
  const monitoring = stats?.monitoring || {};
  
  return (
    <div className="p-6 space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-5 bg-surface border border-border rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Gauge size={22} className="text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-mono font-bold text-text-primary">{monitoring.total || 0}</p>
              <p className="text-xs text-text-tertiary">Total Tests</p>
            </div>
          </div>
        </div>
        
        <div className="p-5 bg-surface border border-border rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
              <Users size={22} className="text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-mono font-bold text-text-primary">{monitoring.manual || 0}</p>
              <p className="text-xs text-text-tertiary">Manual</p>
            </div>
          </div>
        </div>
        
        <div className="p-5 bg-surface border border-border rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <Robot size={22} className="text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-mono font-bold text-text-primary">{monitoring.automated || 0}</p>
              <p className="text-xs text-text-tertiary">Automated</p>
            </div>
          </div>
        </div>
        
        <div className="p-5 bg-surface border border-border rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
              <Clock size={22} className="text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-mono font-bold text-text-primary">{monitoring.due || 0}</p>
              <p className="text-xs text-text-tertiary">Due / Overdue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-surface border border-border rounded-xl p-12 text-center">
        <Gauge size={48} className="mx-auto text-text-tertiary mb-4 opacity-30" />
        <h3 className="font-medium text-text-primary mb-2">Monitoring Tests</h3>
        <p className="text-sm text-text-secondary mb-4 max-w-md mx-auto">
          Configure monitoring tests for your controls. Support for manual and automated monitoring methods.
        </p>
        <p className="text-xs text-text-tertiary">
          Create controls first, then add monitoring tests from the control detail view.
        </p>
      </div>
    </div>
  );
};

// ============================================================
// FINDINGS TAB
// ============================================================

const FindingsTab = ({ findings, onRefresh }) => {
  return (
    <div className="p-6 space-y-4">
      {findings.length > 0 ? (
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-background text-xs text-text-tertiary uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Finding</th>
                <th className="px-4 py-3 text-center w-24">Severity</th>
                <th className="px-4 py-3 text-center w-24">Status</th>
                <th className="px-4 py-3 text-left w-48">Control</th>
                <th className="px-4 py-3 text-center w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {findings.map(finding => (
                <tr key={finding.id} className="hover:bg-background/50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-gold">{finding.finding_id}</span>
                    <p className="text-sm font-medium text-text-primary mt-0.5">{finding.title}</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                      finding.severity === 'critical' ? 'bg-red-500/10 text-red-400' :
                      finding.severity === 'high' ? 'bg-orange-500/10 text-orange-400' :
                      finding.severity === 'medium' ? 'bg-amber-500/10 text-amber-400' :
                      'bg-blue-500/10 text-blue-400'
                    }`}>
                      {finding.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                      finding.status === 'open' ? 'bg-red-500/10 text-red-400' :
                      finding.status === 'in_progress' ? 'bg-amber-500/10 text-amber-400' :
                      finding.status === 'closed' ? 'bg-emerald-500/10 text-emerald-400' :
                      'bg-gray-500/10 text-gray-400'
                    }`}>
                      {finding.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-text-secondary">-</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-text-tertiary">{finding.action_ids?.length || 0}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-xl p-12 text-center">
          <Target size={48} className="mx-auto text-text-tertiary mb-4 opacity-30" />
          <h3 className="font-medium text-text-primary mb-2">No Findings</h3>
          <p className="text-sm text-text-secondary">
            Findings are created when monitoring identifies control weaknesses.
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================
// EVIDENCE TAB
// ============================================================

const EvidenceTab = ({ evidence, requirements, controls, onRefresh }) => {
  const { token } = useAuth();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    evidence_type: 'document',
    context_type: 'requirement',
    context_id: '',
    file_url: ''
  });

  const contextOptions = [
    { value: 'requirement', label: 'Requirement' },
    { value: 'control', label: 'Control' },
    { value: 'finding', label: 'Finding' }
  ];

  const evidenceTypes = [
    { value: 'document', label: 'Document' },
    { value: 'screenshot', label: 'Screenshot' },
    { value: 'attestation', label: 'Attestation' },
    { value: 'system_record', label: 'System Record' },
    { value: 'dataset', label: 'Dataset' },
    { value: 'other', label: 'Other' }
  ];

  const handleUpload = async () => {
    if (!formData.name || !formData.context_id) {
      toast.error('Name and linked object are required');
      return;
    }

    setUploading(true);
    try {
      const params = new URLSearchParams({
        context_type: formData.context_type,
        context_id: formData.context_id,
        name: formData.name,
        evidence_type: formData.evidence_type
      });
      if (formData.description) params.append('description', formData.description);
      if (formData.file_url) params.append('file_url', formData.file_url);

      await axios.post(
        `${API}/crmp/v2/evidence/upload?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Evidence uploaded');
      setShowUploadModal(false);
      setFormData({
        name: '',
        description: '',
        evidence_type: 'document',
        context_type: 'requirement',
        context_id: '',
        file_url: ''
      });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload evidence');
    } finally {
      setUploading(false);
    }
  };

  const deleteEvidence = async (evidenceId) => {
    if (!window.confirm('Delete this evidence?')) return;
    
    try {
      await axios.delete(`${API}/crmp/v2/evidence/${evidenceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evidence deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete evidence');
    }
  };

  const getContextOptions = () => {
    if (formData.context_type === 'requirement') {
      return requirements.map(r => ({ value: r.id, label: `${r.requirement_id}: ${r.title}` }));
    } else if (formData.context_type === 'control') {
      return controls.map(c => ({ value: c.id, label: `${c.control_id}: ${c.name}` }));
    }
    return [];
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text-primary">Evidence Library</h3>
        <button
          onClick={() => setShowUploadModal(true)}
          className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-all flex items-center gap-2"
        >
          <CloudArrowUp size={16} />
          Upload Evidence
        </button>
      </div>

      {evidence.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {evidence.map(item => (
            <div key={item.id} className="p-4 bg-surface border border-border rounded-xl">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gold/10 rounded flex items-center justify-center">
                    <CloudArrowUp size={16} className="text-gold" />
                  </div>
                  <span className="px-2 py-0.5 text-xs rounded bg-blue-500/10 text-blue-400 capitalize">
                    {item.evidence_type?.replace('_', ' ')}
                  </span>
                </div>
                <button
                  onClick={() => deleteEvidence(item.id)}
                  className="text-text-tertiary hover:text-red-400 transition-colors"
                >
                  <Trash size={16} />
                </button>
              </div>
              <h4 className="font-medium text-text-primary">{item.name}</h4>
              {item.description && (
                <p className="text-xs text-text-secondary mt-1 line-clamp-2">{item.description}</p>
              )}
              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs text-text-tertiary">
                <span className="capitalize">{item.context_type}</span>
                <span>{new Date(item.uploaded_at).toLocaleDateString()}</span>
              </div>
              {item.file_url && (
                <a
                  href={item.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 text-xs text-gold hover:underline flex items-center gap-1"
                >
                  <ArrowSquareOut size={12} />
                  View File
                </a>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-xl p-12 text-center">
          <CloudArrowUp size={48} className="mx-auto text-text-tertiary mb-4 opacity-30" />
          <h3 className="font-medium text-text-primary mb-2">No Evidence Yet</h3>
          <p className="text-sm text-text-secondary mb-4">
            Upload documents, screenshots, or attestations to support your compliance activities
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-all inline-flex items-center gap-2"
          >
            <CloudArrowUp size={16} />
            Upload Evidence
          </button>
        </div>
      )}

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowUploadModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white border border-border rounded-xl w-full max-w-lg shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-heading font-medium text-text-primary">Upload Evidence</h2>
                  <button onClick={() => setShowUploadModal(false)} className="text-text-tertiary hover:text-text-primary">
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Evidence Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., CDD Policy Document v2.1"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe this evidence..."
                    rows={2}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Evidence Type</label>
                    <select
                      value={formData.evidence_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, evidence_type: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-1 focus:ring-gold"
                    >
                      {evidenceTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Attach To</label>
                    <select
                      value={formData.context_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, context_type: e.target.value, context_id: '' }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-1 focus:ring-gold"
                    >
                      {contextOptions.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Select {formData.context_type} *</label>
                  <select
                    value={formData.context_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, context_id: e.target.value }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-1 focus:ring-gold"
                  >
                    <option value="">Select...</option>
                    {getContextOptions().map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">File URL (optional)</label>
                  <input
                    type="url"
                    value={formData.file_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, file_url: e.target.value }))}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Link to external document or Azure Blob Storage URL</p>
                </div>
              </div>

              <div className="p-6 border-t border-border flex justify-end gap-3">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 border border-border text-text-secondary rounded-lg hover:bg-background"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 disabled:opacity-50 flex items-center gap-2"
                >
                  {uploading && <ArrowsClockwise size={16} className="animate-spin" />}
                  Upload Evidence
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================
// REGULATORY CHANGES TAB
// ============================================================

const RegulatoryChangesTab = ({ changes, onRefresh }) => {
  const { token } = useAuth();
  const [detecting, setDetecting] = useState(false);
  const [processing, setProcessing] = useState(null);

  const detectChanges = async () => {
    setDetecting(true);
    try {
      const res = await axios.post(
        `${API}/crmp/v2/detect-regulatory-changes`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Detected ${res.data.changes_detected} potential changes`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to detect changes');
    } finally {
      setDetecting(false);
    }
  };

  const reviewChange = async (changeId, status, notes = null) => {
    setProcessing(changeId);
    try {
      const params = new URLSearchParams({ status });
      if (notes) params.append('notes', notes);

      await axios.put(
        `${API}/crmp/v2/regulatory-changes/${changeId}/review?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Change marked as ${status}`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to review change');
    } finally {
      setProcessing(null);
    }
  };

  const pendingChanges = changes.filter(c => c.status === 'pending');
  const reviewedChanges = changes.filter(c => c.status !== 'pending');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Regulatory Change Detection</h3>
          <p className="text-sm text-text-secondary">
            Monitor for regulatory changes that may affect your CRMP requirements
          </p>
        </div>
        <button
          onClick={detectChanges}
          disabled={detecting}
          className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-all flex items-center gap-2 disabled:opacity-50"
        >
          {detecting ? (
            <ArrowsClockwise size={16} className="animate-spin" />
          ) : (
            <Bell size={16} />
          )}
          Scan for Changes
        </button>
      </div>

      {/* Pending Changes Alert */}
      {pendingChanges.length > 0 && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <div className="flex items-start gap-3">
            <Bell size={20} className="text-amber-400 mt-0.5" />
            <div>
              <p className="font-medium text-amber-400">
                {pendingChanges.length} Pending Regulatory Change{pendingChanges.length > 1 ? 's' : ''} Detected
              </p>
              <p className="text-sm text-amber-300 mt-1">
                Review these changes to determine their impact on your CRMP requirements
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pending Changes */}
      {pendingChanges.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-text-primary">Pending Review</h4>
          {pendingChanges.map(change => (
            <div key={change.id} className="p-4 bg-surface border border-amber-500/30 rounded-xl">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-500/10 text-amber-400">
                      Potential Update
                    </span>
                    <span className="text-xs text-text-tertiary">
                      {new Date(change.detected_at).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <p className="text-sm font-medium text-text-primary mb-1">
                    {change.source_document_name}
                  </p>
                  <p className="text-xs text-text-secondary">
                    May affect: <span className="text-gold">{change.affected_requirement_title}</span>
                  </p>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => reviewChange(change.id, 'applied')}
                    disabled={processing === change.id}
                    className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 rounded text-xs font-medium hover:bg-emerald-500/20 transition-all flex items-center gap-1"
                  >
                    <Check size={12} />
                    Apply
                  </button>
                  <button
                    onClick={() => reviewChange(change.id, 'reviewed')}
                    disabled={processing === change.id}
                    className="px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded text-xs font-medium hover:bg-blue-500/20 transition-all flex items-center gap-1"
                  >
                    <Eye size={12} />
                    Note
                  </button>
                  <button
                    onClick={() => reviewChange(change.id, 'dismissed')}
                    disabled={processing === change.id}
                    className="px-3 py-1.5 bg-gray-500/10 text-gray-400 rounded text-xs font-medium hover:bg-gray-500/20 transition-all flex items-center gap-1"
                  >
                    <X size={12} />
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reviewed Changes */}
      {reviewedChanges.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-text-primary">Change History</h4>
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-background text-xs text-text-tertiary uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Source Document</th>
                  <th className="px-4 py-3 text-left">Affected Requirement</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-left">Reviewed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {reviewedChanges.slice(0, 10).map(change => (
                  <tr key={change.id} className="hover:bg-background/50">
                    <td className="px-4 py-3 text-sm text-text-primary">{change.source_document_name}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary">{change.affected_requirement_title}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                        change.status === 'applied' ? 'bg-emerald-500/10 text-emerald-400' :
                        change.status === 'reviewed' ? 'bg-blue-500/10 text-blue-400' :
                        'bg-gray-500/10 text-gray-400'
                      }`}>
                        {change.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-text-tertiary">
                      {change.reviewed_at ? new Date(change.reviewed_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {changes.length === 0 && (
        <div className="bg-surface border border-border rounded-xl p-12 text-center">
          <Bell size={48} className="mx-auto text-text-tertiary mb-4 opacity-30" />
          <h3 className="font-medium text-text-primary mb-2">No Regulatory Changes Detected</h3>
          <p className="text-sm text-text-secondary mb-4">
            Click "Scan for Changes" to detect regulatory updates that may affect your CRMP
          </p>
          <button
            onClick={detectChanges}
            disabled={detecting}
            className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-all inline-flex items-center gap-2"
          >
            {detecting ? <ArrowsClockwise size={16} className="animate-spin" /> : <Bell size={16} />}
            Scan for Changes
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================
// BUILD CRMP MODAL
// ============================================================

const BuildCRMPModal = ({ onClose, onSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [processing, setProcessing] = useState(false);
  const [showUncoveredOnly, setShowUncoveredOnly] = useState(true);

  useEffect(() => {
    fetchUniverseItems();
  }, [showUncoveredOnly]);

  const fetchUniverseItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crmp/v2/universe-items?show_uncovered_only=${showUncoveredOnly}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(res.data.items || []);
    } catch (error) {
      toast.error('Failed to load Regulatory Universe items');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === items.filter(i => !i.is_covered).length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.filter(i => !i.is_covered).map(i => i.id)));
    }
  };

  const handleBulkAdd = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one item');
      return;
    }

    setProcessing(true);
    try {
      const res = await axios.post(`${API}/crmp/v2/requirements/bulk-add-from-universe`, {
        regulatory_universe_item_ids: Array.from(selectedIds)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = res.data;
      toast.success(`Added ${result.created_count} requirements to CRMP`);
      
      if (result.skipped_count > 0) {
        toast.info(`${result.skipped_count} items were already in CRMP`);
      }
      
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add requirements');
    } finally {
      setProcessing(false);
    }
  };

  const uncoveredItems = items.filter(i => !i.is_covered);
  const allSelected = uncoveredItems.length > 0 && selectedIds.size === uncoveredItems.length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white border border-border rounded-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center">
                <PlusCircle size={24} className="text-gold" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-medium text-text-primary">Build CRMP from Regulatory Universe</h2>
                <p className="text-sm text-text-tertiary">Select requirements to add to your Compliance Plan</p>
              </div>
            </div>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="px-6 py-3 border-b border-border flex items-center justify-between bg-background">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showUncoveredOnly}
              onChange={(e) => setShowUncoveredOnly(e.target.checked)}
              className="w-4 h-4 rounded border-border bg-background text-gold focus:ring-gold cursor-pointer"
            />
            <span className="text-sm text-text-secondary">Show uncovered only</span>
          </label>
          
          {uncoveredItems.length > 0 && (
            <button
              onClick={selectAll}
              className="text-sm text-gold hover:underline"
            >
              {allSelected ? 'Deselect All' : `Select All (${uncoveredItems.length})`}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowsClockwise size={32} className="animate-spin text-gold" />
            </div>
          ) : items.length > 0 ? (
            <div className="space-y-3">
              {items.map(item => {
                const isSelected = selectedIds.has(item.id);
                const isCovered = item.is_covered;
                
                return (
                  <div
                    key={item.id}
                    onClick={() => !isCovered && toggleSelect(item.id)}
                    className={`p-4 rounded-lg border transition-all ${
                      isCovered 
                        ? 'bg-background border-border opacity-60 cursor-not-allowed'
                        : isSelected
                          ? 'bg-gold/10 border-gold cursor-pointer'
                          : 'bg-background border-border hover:border-gold/50 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {!isCovered && (
                        <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          isSelected ? 'bg-gold border-gold' : 'border-border'
                        }`}>
                          {isSelected && <Check size={12} className="text-background" />}
                        </div>
                      )}
                      
                      {isCovered && (
                        <div className="w-5 h-5 rounded bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={12} className="text-emerald-400" />
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-text-primary">{item.name}</h4>
                          {isCovered && (
                            <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/10 text-emerald-400">
                              In CRMP
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-text-secondary line-clamp-2">{item.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-text-tertiary">
                          <span>{item.regulator || 'Unknown Regulator'}</span>
                          <span>{item.document_type || 'Document'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <CheckCircle size={48} className="mx-auto text-emerald-400 mb-4" />
              <h3 className="font-medium text-text-primary mb-2">All Covered!</h3>
              <p className="text-sm text-text-secondary">
                All Regulatory Universe items are already in your CRMP
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-between">
          <span className="text-sm text-text-secondary">
            {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-border text-text-secondary rounded-lg hover:bg-background transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkAdd}
              disabled={processing || selectedIds.size === 0}
              className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {processing ? (
                <>
                  <ArrowsClockwise size={16} className="animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus size={16} />
                  Add {selectedIds.size} to CRMP
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================================
// ADD REQUIREMENT MODAL (Manual)
// ============================================================

const AddRequirementModal = ({ onClose, onSuccess }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    regulatory_theme_id: '',
    owner_name: '',
    owner_department: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/crmp/v2/requirements`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Requirement created');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create requirement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white border border-border rounded-xl w-full max-w-lg shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-heading font-medium text-text-primary">Add Requirement Manually</h2>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Customer Due Diligence"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the compliance requirement..."
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Owner</label>
              <input
                type="text"
                value={formData.owner_name}
                onChange={(e) => setFormData(prev => ({ ...prev, owner_name: e.target.value }))}
                placeholder="e.g., Compliance Officer"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Department</label>
              <input
                type="text"
                value={formData.owner_department}
                onChange={(e) => setFormData(prev => ({ ...prev, owner_department: e.target.value }))}
                placeholder="e.g., Compliance"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border text-text-secondary rounded-lg hover:bg-background transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <ArrowsClockwise size={16} className="animate-spin" /> : <Plus size={16} />}
              Create Requirement
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// ============================================================
// ADD CONTROL MODAL
// ============================================================

const AddControlModal = ({ requirements, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    control_type: 'preventive',
    execution: 'manual',
    owner_name: '',
    owner_department: '',
    requirement_ids: []
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.description) {
      toast.error('Name and description are required');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/crmp/v2/controls?${formData.requirement_ids.map(id => `requirement_ids=${id}`).join('&')}`, {
        name: formData.name,
        description: formData.description,
        control_type: formData.control_type,
        execution: formData.execution,
        owner_name: formData.owner_name,
        owner_department: formData.owner_department
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Control created');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create control');
    } finally {
      setSaving(false);
    }
  };

  const toggleRequirement = (id) => {
    setFormData(prev => ({
      ...prev,
      requirement_ids: prev.requirement_ids.includes(id)
        ? prev.requirement_ids.filter(r => r !== id)
        : [...prev.requirement_ids, id]
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white border border-border rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-heading font-medium text-text-primary">Create Institution Control</h2>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Control Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., EDD Approval Before Activation"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this control does and how it mitigates risk..."
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold resize-none"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Control Type</label>
              <select
                value={formData.control_type}
                onChange={(e) => setFormData(prev => ({ ...prev, control_type: e.target.value }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-1 focus:ring-gold"
              >
                <option value="preventive">Preventive</option>
                <option value="detective">Detective</option>
                <option value="corrective">Corrective</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Execution</label>
              <select
                value={formData.execution}
                onChange={(e) => setFormData(prev => ({ ...prev, execution: e.target.value }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-1 focus:ring-gold"
              >
                <option value="manual">Manual</option>
                <option value="automated">Automated</option>
                <option value="system_assisted">System-Assisted</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Owner</label>
              <input
                type="text"
                value={formData.owner_name}
                onChange={(e) => setFormData(prev => ({ ...prev, owner_name: e.target.value }))}
                placeholder="e.g., Financial Crime Operations"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Department</label>
              <input
                type="text"
                value={formData.owner_department}
                onChange={(e) => setFormData(prev => ({ ...prev, owner_department: e.target.value }))}
                placeholder="e.g., Operations"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold"
              />
            </div>
          </div>

          {/* Link to Requirements */}
          {requirements.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Link to Requirements</label>
              <div className="max-h-40 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
                {requirements.map(req => (
                  <label
                    key={req.id}
                    className="flex items-center gap-2 p-2 hover:bg-background rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.requirement_ids.includes(req.id)}
                      onChange={() => toggleRequirement(req.id)}
                      className="w-4 h-4 rounded border-border bg-background text-gold focus:ring-gold cursor-pointer"
                    />
                    <span className="text-sm text-text-primary">{req.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </form>

        <div className="p-6 border-t border-border flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-border text-text-secondary rounded-lg hover:bg-background transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <ArrowsClockwise size={16} className="animate-spin" /> : <Plus size={16} />}
            Create Control
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================================
// REQUIREMENT DETAIL DRAWER - Enhanced with Risk, NAVI, Theme
// ============================================================

const RequirementDetailDrawer = ({ requirementId, onClose, onRefresh }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddRiskModal, setShowAddRiskModal] = useState(false);
  const [showNaviSuggestions, setShowNaviSuggestions] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [naviSuggestions, setNaviSuggestions] = useState([]);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);

  useEffect(() => {
    fetchRequirementDetail();
  }, [requirementId]);

  const fetchRequirementDetail = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crmp/v2/requirements/${requirementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      
      // Fetch existing NAVI suggestions
      const suggestionsRes = await axios.get(`${API}/crmp/v2/suggested-controls?requirement_id=${requirementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNaviSuggestions(suggestionsRes.data.suggestions || []);
    } catch (error) {
      toast.error('Failed to load requirement details');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const generateNaviSuggestions = async () => {
    setGeneratingSuggestions(true);
    try {
      const res = await axios.post(
        `${API}/crmp/v2/requirements/${requirementId}/generate-suggested-controls`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNaviSuggestions(res.data.suggestions || []);
      toast.success(`Generated ${res.data.suggestions_count} control suggestions`);
      setShowNaviSuggestions(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate suggestions');
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  const adoptSuggestion = async (suggestionId) => {
    try {
      await axios.post(
        `${API}/crmp/v2/suggested-controls/${suggestionId}/adopt?requirement_ids=${requirementId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Control adopted successfully');
      fetchRequirementDetail();
      onRefresh();
    } catch (error) {
      toast.error('Failed to adopt control');
    }
  };

  const dismissSuggestion = async (suggestionId) => {
    try {
      await axios.post(
        `${API}/crmp/v2/suggested-controls/${suggestionId}/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNaviSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      toast.success('Suggestion dismissed');
    } catch (error) {
      toast.error('Failed to dismiss suggestion');
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 50 }}
        className="fixed inset-y-0 right-0 w-[700px] bg-surface border-l border-border shadow-2xl z-40 flex items-center justify-center"
      >
        <ArrowsClockwise size={32} className="animate-spin text-gold" />
      </motion.div>
    );
  }

  const req = data?.requirement;
  const risks = data?.risks || [];
  const controls = data?.controls || [];
  const findings = data?.findings || [];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'risks', label: 'Risks', count: risks.length },
    { id: 'controls', label: 'Controls', count: controls.length },
    { id: 'navi', label: 'NAVI Suggestions', count: naviSuggestions.length }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      className="fixed inset-y-0 right-0 w-[700px] bg-surface border-l border-border shadow-2xl z-40 flex flex-col"
    >
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-all"
          >
            <ArrowLeft size={20} />
            <span className="text-sm">Back</span>
          </button>
          <button
            onClick={() => setShowThemeModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-text-secondary hover:text-gold hover:bg-gold/10 rounded-lg transition-all"
          >
            <Tag size={16} />
            {req?.regulatory_theme_name || req?.regulatory_theme_id || 'Set Theme'}
          </button>
        </div>
        
        <div>
          <span className="text-xs font-mono text-gold">{req?.requirement_id}</span>
          <h2 className="text-xl font-heading font-semibold text-text-primary mt-1">{req?.title}</h2>
          {req?.description && (
            <p className="text-sm text-text-secondary mt-2 line-clamp-2">{req.description}</p>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-4 -mb-6 pb-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-background text-gold border-t border-x border-border'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded bg-background">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Regulatory Basis */}
            <div>
              <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                <Globe size={16} className="text-gold" />
                Regulatory Basis
              </h3>
              {req?.regulatory_basis?.length > 0 ? (
                <div className="space-y-2">
                  {req.regulatory_basis.map((basis, idx) => (
                    <div key={idx} className="p-3 bg-background border border-border rounded-lg">
                      <p className="text-sm font-medium text-text-primary">{basis.document_name || 'Source Document'}</p>
                      {basis.section_reference && (
                        <p className="text-xs text-gold mt-1">{basis.section_reference}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-text-tertiary">No regulatory basis linked</p>
              )}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-background border border-border rounded-lg text-center">
                <p className="text-2xl font-bold text-orange-400">{risks.length}</p>
                <p className="text-xs text-text-tertiary">Risks</p>
              </div>
              <div className="p-3 bg-background border border-border rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-400">{controls.length}</p>
                <p className="text-xs text-text-tertiary">Controls</p>
              </div>
              <div className="p-3 bg-background border border-border rounded-lg text-center">
                <p className="text-2xl font-bold text-red-400">{findings.length}</p>
                <p className="text-xs text-text-tertiary">Findings</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowAddRiskModal(true)}
                className="px-3 py-2 bg-orange-500/10 text-orange-400 rounded-lg text-sm font-medium hover:bg-orange-500/20 transition-all flex items-center gap-2"
              >
                <Warning size={16} />
                Add Risk
              </button>
              <button
                onClick={generateNaviSuggestions}
                disabled={generatingSuggestions}
                className="px-3 py-2 bg-purple-500/10 text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-500/20 transition-all flex items-center gap-2 disabled:opacity-50"
              >
                {generatingSuggestions ? (
                  <ArrowsClockwise size={16} className="animate-spin" />
                ) : (
                  <Sparkle size={16} />
                )}
                NAVI Suggest Controls
              </button>
            </div>
          </div>
        )}

        {activeTab === 'risks' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-text-primary">Identified Risks</h3>
              <button
                onClick={() => setShowAddRiskModal(true)}
                className="px-3 py-1.5 bg-gold text-background rounded-lg text-sm font-medium hover:bg-gold/90 transition-all flex items-center gap-2"
              >
                <Plus size={14} />
                Add Risk
              </button>
            </div>

            {risks.length > 0 ? (
              <div className="space-y-3">
                {risks.map(risk => (
                  <RiskCard key={risk.id} risk={risk} onRefresh={fetchRequirementDetail} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Warning size={40} className="mx-auto text-text-tertiary mb-3 opacity-30" />
                <p className="text-sm text-text-tertiary">No risks identified yet</p>
                <p className="text-xs text-text-tertiary mt-1">Add risks to document what could go wrong</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'controls' && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-text-primary">Linked Controls</h3>
            {controls.length > 0 ? (
              <div className="space-y-3">
                {controls.map(control => (
                  <div key={control.id} className="p-4 bg-background border border-border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-mono text-gold">{control.control_id}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                        control.status === 'implemented' ? 'bg-emerald-500/10 text-emerald-400' :
                        'bg-amber-500/10 text-amber-400'
                      }`}>
                        {control.status?.replace('_', ' ')}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                        control.effectiveness_rating === 'effective' ? 'bg-emerald-500/10 text-emerald-400' :
                        control.effectiveness_rating === 'partially_effective' ? 'bg-amber-500/10 text-amber-400' :
                        control.effectiveness_rating === 'ineffective' ? 'bg-red-500/10 text-red-400' :
                        'bg-gray-500/10 text-gray-400'
                      }`}>
                        {control.effectiveness_rating?.replace('_', ' ') || 'Not Tested'}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-text-primary">{control.name}</p>
                    <p className="text-xs text-text-secondary mt-1">{control.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ShieldCheck size={40} className="mx-auto text-text-tertiary mb-3 opacity-30" />
                <p className="text-sm text-text-tertiary">No controls linked yet</p>
                <p className="text-xs text-text-tertiary mt-1">Create controls from the Controls tab</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'navi' && (
          <div className="space-y-4">
            <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <Sparkle size={18} className="text-purple-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-purple-400">AI-Suggested Controls</p>
                  <p className="text-xs text-purple-300 mt-1">
                    These are suggestions for consideration only. They do NOT represent implemented controls until explicitly adopted.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-text-primary">Suggestions ({naviSuggestions.length})</h3>
              <button
                onClick={generateNaviSuggestions}
                disabled={generatingSuggestions}
                className="px-3 py-1.5 bg-purple-500/10 text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-500/20 transition-all flex items-center gap-2 disabled:opacity-50"
              >
                {generatingSuggestions ? (
                  <ArrowsClockwise size={14} className="animate-spin" />
                ) : (
                  <Sparkle size={14} />
                )}
                Generate More
              </button>
            </div>

            {naviSuggestions.length > 0 ? (
              <div className="space-y-3">
                {naviSuggestions.map(suggestion => (
                  <div key={suggestion.id} className="p-4 bg-background border border-purple-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-500/10 text-purple-400">
                        Suggested
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/10 text-blue-400 capitalize">
                        {suggestion.control_type}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-text-primary">{suggestion.name}</p>
                    <p className="text-xs text-text-secondary mt-1">{suggestion.description}</p>
                    
                    {suggestion.suggested_owner_role && (
                      <p className="text-xs text-text-tertiary mt-2">
                        Suggested Owner: {suggestion.suggested_owner_role}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => adoptSuggestion(suggestion.id)}
                        className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 rounded text-xs font-medium hover:bg-emerald-500/20 transition-all flex items-center gap-1"
                      >
                        <Check size={12} />
                        Adopt as Control
                      </button>
                      <button
                        onClick={() => dismissSuggestion(suggestion.id)}
                        className="px-3 py-1.5 bg-red-500/10 text-red-400 rounded text-xs font-medium hover:bg-red-500/20 transition-all flex items-center gap-1"
                      >
                        <X size={12} />
                        Dismiss
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Sparkle size={40} className="mx-auto text-purple-400 mb-3 opacity-30" />
                <p className="text-sm text-text-tertiary">No suggestions yet</p>
                <p className="text-xs text-text-tertiary mt-1">Click "Generate More" to get AI-suggested controls</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Risk Modal */}
      <AnimatePresence>
        {showAddRiskModal && (
          <AddRiskModal
            requirementId={requirementId}
            onClose={() => setShowAddRiskModal(false)}
            onSuccess={() => {
              setShowAddRiskModal(false);
              fetchRequirementDetail();
              onRefresh();
            }}
          />
        )}
      </AnimatePresence>

      {/* Theme Selection Modal */}
      <AnimatePresence>
        {showThemeModal && (
          <ThemeSelectionModal
            requirementId={requirementId}
            currentThemeId={req?.regulatory_theme_id}
            onClose={() => setShowThemeModal(false)}
            onSuccess={() => {
              setShowThemeModal(false);
              fetchRequirementDetail();
              onRefresh();
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Risk Card Component
const RiskCard = ({ risk, onRefresh }) => {
  const { token } = useAuth();
  const [calculating, setCalculating] = useState(false);

  const calculateResidual = async () => {
    setCalculating(true);
    try {
      const res = await axios.put(
        `${API}/crmp/v2/risks/${risk.id}/calculate-residual`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Residual risk: ${res.data.residual.rating}`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate residual risk');
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="p-4 bg-background border border-border rounded-lg">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-sm font-medium text-text-primary">{risk.title}</p>
          <p className="text-xs text-text-secondary mt-1">{risk.description}</p>
        </div>
      </div>
      
      {/* Risk Scores */}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="p-2 bg-surface border border-border rounded">
          <p className="text-xs text-text-tertiary">Inherent Risk</p>
          <div className="flex items-center gap-2 mt-1">
            {risk.inherent_risk_rating ? (
              <>
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                  risk.inherent_risk_rating === 'High' ? 'bg-red-500/10 text-red-400' :
                  risk.inherent_risk_rating === 'Medium' ? 'bg-amber-500/10 text-amber-400' :
                  'bg-blue-500/10 text-blue-400'
                }`}>
                  {risk.inherent_risk_rating}
                </span>
                <span className="text-xs text-text-tertiary">
                  ({risk.inherent_likelihood} × {risk.inherent_impact} = {risk.inherent_risk_score})
                </span>
              </>
            ) : (
              <span className="text-xs text-text-tertiary">Not assessed</span>
            )}
          </div>
        </div>
        
        <div className="p-2 bg-surface border border-border rounded">
          <p className="text-xs text-text-tertiary">Residual Risk</p>
          <div className="flex items-center gap-2 mt-1">
            {risk.residual_risk_rating ? (
              <>
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                  risk.residual_risk_rating === 'High' ? 'bg-red-500/10 text-red-400' :
                  risk.residual_risk_rating === 'Medium' ? 'bg-amber-500/10 text-amber-400' :
                  'bg-blue-500/10 text-blue-400'
                }`}>
                  {risk.residual_risk_rating}
                </span>
                <span className="text-xs text-text-tertiary">
                  ({risk.residual_risk_score})
                </span>
              </>
            ) : (
              <button
                onClick={calculateResidual}
                disabled={calculating || !risk.inherent_risk_rating}
                className="text-xs text-gold hover:underline disabled:opacity-50 disabled:no-underline"
              >
                {calculating ? 'Calculating...' : 'Calculate'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Add Risk Modal
const AddRiskModal = ({ requirementId, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    risk_category: '',
    inherent_likelihood: 3,
    inherent_impact: 3
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      toast.error('Title and description are required');
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams({
        title: formData.title,
        description: formData.description,
        inherent_likelihood: formData.inherent_likelihood,
        inherent_impact: formData.inherent_impact
      });
      if (formData.risk_category) {
        params.append('risk_category', formData.risk_category);
      }

      await axios.post(
        `${API}/crmp/v2/requirements/${requirementId}/risks?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Risk created');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create risk');
    } finally {
      setSaving(false);
    }
  };

  const riskScore = formData.inherent_likelihood * formData.inherent_impact;
  const riskRating = riskScore >= 15 ? 'High' : riskScore >= 7 ? 'Medium' : 'Low';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white border border-border rounded-xl w-full max-w-lg shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-heading font-medium text-text-primary">Add Compliance Risk</h2>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Risk Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Inadequate customer screening"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what could go wrong if this requirement is not met..."
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold resize-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Risk Category</label>
            <select
              value={formData.risk_category}
              onChange={(e) => setFormData(prev => ({ ...prev, risk_category: e.target.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-1 focus:ring-gold"
            >
              <option value="">Select category...</option>
              <option value="regulatory">Regulatory</option>
              <option value="financial">Financial</option>
              <option value="reputational">Reputational</option>
              <option value="operational">Operational</option>
              <option value="legal">Legal</option>
            </select>
          </div>

          {/* Risk Assessment */}
          <div className="p-4 bg-background border border-border rounded-lg">
            <h4 className="text-sm font-semibold text-text-primary mb-3">Inherent Risk Assessment</h4>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-text-secondary">Likelihood</label>
                  <span className="text-sm font-mono text-gold">{formData.inherent_likelihood}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={formData.inherent_likelihood}
                  onChange={(e) => setFormData(prev => ({ ...prev, inherent_likelihood: parseInt(e.target.value) }))}
                  className="w-full accent-gold"
                />
                <div className="flex justify-between text-xs text-text-tertiary mt-1">
                  <span>Rare</span>
                  <span>Almost Certain</span>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-text-secondary">Impact</label>
                  <span className="text-sm font-mono text-gold">{formData.inherent_impact}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={formData.inherent_impact}
                  onChange={(e) => setFormData(prev => ({ ...prev, inherent_impact: parseInt(e.target.value) }))}
                  className="w-full accent-gold"
                />
                <div className="flex justify-between text-xs text-text-tertiary mt-1">
                  <span>Negligible</span>
                  <span>Severe</span>
                </div>
              </div>

              {/* Risk Score Display */}
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="text-sm text-text-secondary">Inherent Risk Score</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-mono font-bold text-text-primary">{riskScore}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                    riskRating === 'High' ? 'bg-red-500/10 text-red-400' :
                    riskRating === 'Medium' ? 'bg-amber-500/10 text-amber-400' :
                    'bg-blue-500/10 text-blue-400'
                  }`}>
                    {riskRating}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border text-text-secondary rounded-lg hover:bg-background transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <ArrowsClockwise size={16} className="animate-spin" /> : <Plus size={16} />}
              Add Risk
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Theme Selection Modal
const ThemeSelectionModal = ({ requirementId, currentThemeId, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [themes, setThemes] = useState({ custom_themes: [], standard_themes: [] });
  const [selectedTheme, setSelectedTheme] = useState(currentThemeId || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchThemes();
  }, []);

  const fetchThemes = async () => {
    try {
      const res = await axios.get(`${API}/crmp/v2/themes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThemes(res.data);
    } catch (error) {
      console.error('Failed to fetch themes');
    }
  };

  const handleSave = async () => {
    if (!selectedTheme) {
      toast.error('Please select a theme');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/crmp/v2/requirements/${requirementId}/theme?theme_id=${selectedTheme}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Theme updated');
      onSuccess();
    } catch (error) {
      toast.error('Failed to update theme');
    } finally {
      setSaving(false);
    }
  };

  const allThemes = [...themes.custom_themes, ...themes.standard_themes];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white border border-border rounded-xl w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-heading font-medium text-text-primary">Select Regulatory Theme</h2>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 max-h-80 overflow-y-auto space-y-2">
          {allThemes.map(theme => (
            <label
              key={theme.id}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                selectedTheme === theme.id
                  ? 'bg-gold/10 border border-gold'
                  : 'bg-background border border-border hover:border-gold/50'
              }`}
            >
              <input
                type="radio"
                name="theme"
                value={theme.id}
                checked={selectedTheme === theme.id}
                onChange={() => setSelectedTheme(theme.id)}
                className="w-4 h-4 accent-gold"
              />
              <div>
                <p className="text-sm font-medium text-text-primary">{theme.label}</p>
                {theme.description && (
                  <p className="text-xs text-text-tertiary">{theme.description}</p>
                )}
              </div>
            </label>
          ))}
        </div>

        <div className="p-6 border-t border-border flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border text-text-secondary rounded-lg hover:bg-background"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !selectedTheme}
            className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <ArrowsClockwise size={16} className="animate-spin" />}
            Save Theme
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CRMPV2Page;
